import { useEffect, useState } from "react";
import { ApiError, fetchReviewCase, fetchReviewQueue, resolveReviewCase } from "../api";
import { completeMission } from "../game";
import type {
  ReviewAction,
  ReviewCaseDetail,
  ReviewCaseSummary,
  ReviewResolveResponse,
} from "../types";

/**
 * MISSION 5: the visitor sits in the reviewer's chair.
 *
 * Everything shown is computed or recorded server-side: case scores come from the live
 * engine (the queue endpoint refuses to show anything the policy would not actually send
 * to a human), and the "N명이 처리, X%가 사기 판단" line is a real aggregate over what
 * other visitors did - rendered only from server responses, never from local math.
 * Design + falsification criteria: docs/REVIEW_QUEUE_DESIGN.md.
 */

const REASON_LABELS: Record<ReviewAction, { value: string; label: string }[]> = {
  CONFIRM_LEGIT: [
    { value: "PATTERN_LOOKS_NORMAL", label: "평소 거래 패턴과 자연스러움" },
    { value: "PLAUSIBLE_EXPLANATION", label: "상황이 설명 가능함" },
    { value: "SIGNALS_TOO_WEAK", label: "위험 신호가 결정적이지 않음" },
  ],
  CONFIRM_FRAUD: [
    { value: "CLASSIC_FRAUD_PATTERN", label: "전형적인 사기 패턴" },
    { value: "TOO_MANY_SIGNALS", label: "위험 신호가 너무 많음" },
    { value: "BETTER_SAFE_THAN_SORRY", label: "의심스러울 땐 막는 게 안전" },
  ],
};

const REASON_CODE_LABELS: Record<string, string> = {
  RISK_SCORE_ABOVE_REVIEW_THRESHOLD: "위험 점수가 검토 기준선을 넘음",
};

function aggregateLine(total: number, fraudCount: number): string {
  if (total === 0) return "🥇 당신이 이 케이스의 첫 검토자예요!";
  const fraudShare = Math.round((fraudCount / total) * 100);
  return `지금까지 방문자 ${total.toLocaleString()}명이 이 케이스를 처리했고, ${fraudShare}%가 사기로 판단했어요.`;
}

export function ReviewQueue() {
  const [queue, setQueue] = useState<ReviewCaseSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReviewCaseDetail | null>(null);
  const [action, setAction] = useState<ReviewAction | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ReviewResolveResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [resolvedCases, setResolvedCases] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchReviewQueue()
      .then(setQueue)
      .catch((err: unknown) => {
        setLoadError(
          err instanceof ApiError ? err.message : "검토 대기열을 불러오지 못했습니다.",
        );
      });
  }, []);

  async function openCase(caseId: string) {
    setBusy(true);
    setReceipt(null);
    setAction(null);
    setReason(null);
    try {
      setDetail(await fetchReviewCase(caseId));
    } catch {
      setLoadError("케이스를 불러오지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!detail || !action || !reason) return;
    setBusy(true);
    try {
      const response = await resolveReviewCase(detail.case_id, action, reason);
      setReceipt(response);
      setResolvedCases((prev) => new Set(prev).add(detail.case_id));
      // A verdict was really recorded in the reviewer ledger: mission 5 verified.
      completeMission("try-review");
    } catch {
      setLoadError("처리를 기록하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <section className="card">
        <h3>🧑‍💼 은행원이 되어보기</h3>
        <p className="error-text">{loadError}</p>
      </section>
    );
  }
  if (!queue) {
    return (
      <section className="card">
        <h3>🧑‍💼 은행원이 되어보기</h3>
        <p className="muted">검토 대기열을 불러오는 중…</p>
      </section>
    );
  }

  return (
    <section className="card">
      <h3>🧑‍💼 은행원이 되어보기 — 이번엔 당신이 판정할 차례</h3>
      <p>
        AI가 &quot;사람이 봐야 한다&quot;고 넘긴 거래들이에요 (가상의 케이스지만, 위험
        점수는 <strong>지금 이 서버의 진짜 엔진</strong>이 방금 계산한 값입니다). 위험한
        것부터 정렬돼 있어요. 하나 골라서 판정 근거를 읽고, 승인할지 막을지 결정해
        보세요 — <strong>당신의 결정은 실제 데이터베이스에 기록됩니다.</strong>
      </p>

      <div className="review-queue">
        {queue.map((item) => (
          <button
            key={item.case_id}
            type="button"
            className={`review-case ${detail?.case_id === item.case_id ? "selected" : ""}`}
            disabled={busy}
            onClick={() => void openCase(item.case_id)}
          >
            <span className="review-case-title">
              {item.title}
              {resolvedCases.has(item.case_id) && <span className="review-done">처리함 ✓</span>}
            </span>
            <span className="review-case-summary">{item.summary}</span>
            <span className="review-case-score">
              위험 점수 {item.risk_score.toFixed(4)} (기준선{" "}
              {item.review_threshold.toFixed(4)})
            </span>
          </button>
        ))}
      </div>

      {detail && (
        <div className="review-detail">
          <h4>
            {detail.title} — 판정 근거 (장부 그대로)
          </h4>
          <ul className="plain">
            <li>
              <strong>위험 점수</strong> {detail.risk_score.toFixed(4)} ≥ 검토 기준선{" "}
              {detail.review_threshold.toFixed(4)}
            </li>
            <li>
              <strong>거래 내용</strong> {detail.summary}
            </li>
            <li>
              <strong>판정 이유</strong>{" "}
              {detail.reason_codes
                .map((code) => REASON_CODE_LABELS[code] ?? code)
                .join(", ")}
            </li>
            <li className="muted">
              판정한 버전: model {detail.versions.model} · feature {detail.versions.feature} ·
              policy {detail.versions.policy}
            </li>
          </ul>
          <p className="muted">
            {aggregateLine(detail.aggregate.resolutions_total, detail.aggregate.fraud_count)}
          </p>

          {receipt ? (
            <div className="review-receipt">
              <strong>🧾 장부에 기록됐습니다</strong>
              <p>
                처리 번호 <code>{receipt.resolution_id.slice(0, 8)}…</code> ·{" "}
                {receipt.action === "CONFIRM_FRAUD" ? "🚫 사기로 차단" : "✅ 정상으로 승인"}{" "}
                — 원래 판정은 수정되지 않고, 당신의 결정이 별도 장부에 추가됐어요.
              </p>
              <p className="muted">
                {aggregateLine(
                  receipt.aggregate.resolutions_total,
                  receipt.aggregate.fraud_count,
                )}
              </p>
            </div>
          ) : (
            <div className="review-actions">
              <div className="review-action-row">
                <button
                  type="button"
                  className={`review-action legit ${action === "CONFIRM_LEGIT" ? "selected" : ""}`}
                  onClick={() => {
                    setAction("CONFIRM_LEGIT");
                    setReason(null);
                  }}
                >
                  ✅ 정상 거래로 승인
                </button>
                <button
                  type="button"
                  className={`review-action fraud ${action === "CONFIRM_FRAUD" ? "selected" : ""}`}
                  onClick={() => {
                    setAction("CONFIRM_FRAUD");
                    setReason(null);
                  }}
                >
                  🚫 사기로 차단
                </button>
              </div>
              {action && (
                <div className="review-reasons">
                  <span className="choice-label">이유 (하나 선택)</span>
                  <div className="choice-buttons">
                    {REASON_LABELS[action].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`choice ${reason === option.value ? "selected" : ""}`}
                        onClick={() => setReason(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="send-button"
                disabled={!action || !reason || busy}
                onClick={() => void submit()}
              >
                {busy ? "기록 중…" : "이대로 처리하기"}
              </button>
            </div>
          )}
        </div>
      )}

      <p className="muted">
        실제 은행이라면 이 화면 뒤에 검토자 계정·권한 체계가 붙어요. 그 설계는{" "}
        <code>docs/REVIEW_QUEUE_DESIGN.md</code>에 있고, 여기서는 결정권이 사람에게
        남는다는 구조 자체를 체험하는 게 목적입니다.
      </p>
    </section>
  );
}
